// User types
export interface User {
  id: string;
  email: string;
  full_name: string;
  role: string;
  is_active: boolean;
  free_reports_remaining: number | null;
  llm_model?: string | null; // admin-set report model; null = system default
  created_at: string;
}

export interface Token {
  access_token: string;
  token_type: string;
  user: User;
}

// Report types
// Note: llm_model on User is admin-managed (Customers tab); the reporter UI
// never surfaces it.
export interface ValidationIssue {
  id: string;
  category: 'laterality' | 'anatomy' | 'measurement' | 'consistency' | 'completeness' | 'terminology' | 'hallucination' | 'dictation';
  severity: 'error' | 'warning' | 'info';
  message: string;
  details: string;
  location?: string;
  suggestion?: string;
  auto_fixable: boolean;
  fix_value?: string;
}

export interface ReportGenerateRequest {
  transcription: string;
  modality?: string;  // Auto-extracted if not provided
  body_part?: string;  // Auto-extracted if not provided
  clinical_history?: string;  // Auto-extracted if not provided
  patient_identifier?: string;
  study_date?: string;
  model?: string;  // LLM model to use
  template_id?: string;  // Manual template override
  force_primary?: boolean;  // Skip fallback chain (used by "retry with primary")
}

// Available LLM models
export const LLM_MODELS = [
  { value: 'claude-opus-4-6', label: 'Claude Opus 4.6' },
  { value: 'gpt-5.2', label: 'GPT-5.2 Thinking' },
  { value: 'gpt-5.2-chat-latest', label: 'GPT-5.2 Instant' },
  { value: 'claude-opus-4-5-20251101', label: 'Claude Opus 4.5' },
  { value: 'gpt-4o', label: 'GPT-4o' },
  { value: 'gpt-4o-mini', label: 'GPT-4o Mini (Fast)' },
] as const;

// Models used in the side-by-side comparison view
export const COMPARISON_MODELS = [
  { value: 'gpt-5.2-chat-latest', label: 'GPT-5.2 Instant', description: 'Fast, non-reasoning — likely what ChatGPT uses' },
  { value: 'gpt-5.2', label: 'GPT-5.2 Thinking', description: 'Reasoning model, deliberate output' },
  { value: 'claude-opus-4-5-20251101', label: 'Claude Opus 4.5', description: 'Anthropic flagship model' },
  { value: 'claude-opus-4-6', label: 'Claude Opus 4.6', description: 'Anthropic latest flagship model' },
] as const;

export interface ReportGenerateResponse {
  report: string;
  raw_report: string;  // Original LLM output before processing
  debug_prompt?: string;  // System prompt sent to OpenAI
  debug_user_message?: string;  // User message sent to OpenAI
  issues: ValidationIssue[];
  modality: string;
  body_part?: string;
  // Template classification fields
  exam_category?: string;
  template_id?: string;
  template_name?: string;
  decision_id?: string;
  classification_method?: string;
  classification_confidence?: number;
  // Fallback chain transparency — set when the backend has the fix
  primary_model?: string;  // Configured primary (target of "retry with primary")
  model_used?: string;     // Model that actually produced the output
}

// ─── Report Profiles (onboarding) ──────────────────────────────────

export interface StyleConfig {
  structure?: 'narrative' | 'itemized';
  section_order?: string[];
  use_subheadings?: boolean;
  conclusion_style?: 'numbered' | 'prose';
  heading_case?: 'title' | 'upper';
}

export interface VocabSub {
  from: string;
  to: string;
}

export interface Abbreviation {
  abbr: string;
  expansion: string;
}

export interface LanguageConfig {
  vocabulary?: VocabSub[];
  preferred_phrases?: string[];
  banned_phrases?: string[];
  abbreviations?: Abbreviation[];
}

export interface ReportProfile {
  id: string;
  user_id: string;
  exam_category: string;
  template_id?: string;
  style_config: StyleConfig;
  language_config: LanguageConfig;
  created_at: string;
  updated_at: string;
}

export interface EffectiveConfig {
  exam_category: string;
  has_profile: boolean;
  template_id?: string;
  style_config: StyleConfig;
  language_config: LanguageConfig;
}

export interface ProfileUpsert {
  template_id?: string | null;
  style_config?: StyleConfig;
  language_config?: LanguageConfig;
}

// Pass 1 of the 2-pass flow — the template recommendation for the gate.
export interface ClassifyResponse {
  exam_category?: string;
  template_id?: string;
  template_name?: string;
  classification_method?: string;
  classification_confidence?: number;
  recommended_source: 'classifier' | 'profile';
  has_profile: boolean;
}

// ─── Feedback ──────────────────────────────────────────────────────

export type FeedbackCategory =
  | 'critical'        // app is broken — 500s, can't generate, blocking
  | 'model_quality'   // report quality, same errors repeating, not learning
  | 'ui_bug'          // UI bug — copy button, layout, etc.
  | 'ease_of_life'    // workflow improvement / feature request
  | 'other';

// Subcategory: only relevant when category === 'model_quality'. Forces the
// user to bucket their complaint into an error class that maps to a detector
// (or future detector) so signal/feedback correlation is mechanical.
export type FeedbackSubcategory =
  | 'hallucinated_finding'
  | 'contrast_vocab'
  | 'laterality_flip'
  | 'polarity_flip'
  | 'missing_finding'
  | 'wrong_measurement'
  | 'wrong_section'
  | 'style_mismatch'
  | 'other';

export type FeedbackStatus = 'new' | 'in_progress' | 'resolved' | 'wont_fix';

export interface FeedbackCreate {
  category: FeedbackCategory;
  subcategory?: FeedbackSubcategory;
  content: string;
  // Session context — frontend fills in what it has
  transcription?: string;
  raw_report?: string;
  final_report?: string;
  model_used?: string;
  primary_model?: string;
  exam_category?: string;
  template_id?: string;
  template_name?: string;
  decision_id?: string;
  // Browser context
  url?: string;
  user_agent?: string;
  viewport_width?: number;
  viewport_height?: number;
  // Data URL "data:image/png;base64,..."
  screenshot?: string;
}

export interface Feedback {
  id: string;
  user_id: string;
  user_email?: string;
  category: FeedbackCategory;
  subcategory?: FeedbackSubcategory;
  content: string;
  status: FeedbackStatus;
  admin_notes?: string;
  transcription?: string;
  raw_report?: string;
  final_report?: string;
  model_used?: string;
  primary_model?: string;
  exam_category?: string;
  template_id?: string;
  template_name?: string;
  decision_id?: string;
  url?: string;
  user_agent?: string;
  viewport_width?: number;
  viewport_height?: number;
  has_screenshot: boolean;
  created_at: string;
  updated_at: string;
  resolved_at?: string;
}

export interface FeedbackUpdate {
  status?: FeedbackStatus;
  admin_notes?: string;
}

export const FEEDBACK_CATEGORIES: { value: FeedbackCategory; label: string; description: string }[] = [
  { value: 'critical', label: 'Critical issue', description: 'App is broken or unusable (500 errors, lost work)' },
  { value: 'model_quality', label: 'Report quality / model not learning', description: 'Same mistakes keep happening, output is wrong' },
  { value: 'ui_bug', label: 'UI bug', description: 'Button, layout, or interaction not working as intended' },
  { value: 'ease_of_life', label: 'Ease-of-life / feature request', description: 'Something that would make the workflow smoother' },
  { value: 'other', label: 'Other', description: 'Anything else' },
];

export const FEEDBACK_CATEGORY_LABEL: Record<FeedbackCategory, string> = {
  critical: 'Critical issue',
  model_quality: 'Report quality',
  ui_bug: 'UI bug',
  ease_of_life: 'Ease of life',
  other: 'Other',
};

export const FEEDBACK_STATUS_LABEL: Record<FeedbackStatus, string> = {
  new: 'New',
  in_progress: 'In progress',
  resolved: 'Resolved',
  wont_fix: "Won't fix",
};

// Subcategory options shown only when category=model_quality
export const FEEDBACK_SUBCATEGORIES: { value: FeedbackSubcategory; label: string; description: string }[] = [
  { value: 'hallucinated_finding', label: 'Added a finding I didn\'t dictate', description: 'Report includes something that wasn\'t in your dictation' },
  { value: 'contrast_vocab', label: 'Wrong contrast / sequence language', description: 'e.g. mentions "enhancement" in a non-contrast study' },
  { value: 'laterality_flip', label: 'Laterality wrong', description: 'Says left when you said right (or vice versa)' },
  { value: 'polarity_flip', label: 'Negation flipped', description: 'Says "no X" when X was present, or vice versa' },
  { value: 'missing_finding', label: 'Missed a finding I dictated', description: 'Something you said is not in the report' },
  { value: 'wrong_measurement', label: 'Wrong measurement / number', description: 'Size, score, or grade is wrong' },
  { value: 'wrong_section', label: 'Wrong section placement', description: 'Finding ended up in Conclusion when it should be in Findings, etc.' },
  { value: 'style_mismatch', label: 'Style / tone / verbosity', description: 'Doesn\'t match how you normally report' },
  { value: 'other', label: 'Other', description: 'Something else — describe in the text box' },
];

export const FEEDBACK_SUBCATEGORY_LABEL: Record<FeedbackSubcategory, string> = {
  hallucinated_finding: 'Hallucinated finding',
  contrast_vocab: 'Wrong contrast vocab',
  laterality_flip: 'Laterality flip',
  polarity_flip: 'Polarity flip',
  missing_finding: 'Missing finding',
  wrong_measurement: 'Wrong measurement',
  wrong_section: 'Wrong section',
  style_mismatch: 'Style mismatch',
  other: 'Other',
};

// ─── Learning engine ───────────────────────────────────────────────

export type StyleRuleState = 'candidate' | 'active' | 'retracted';
export type StyleRuleSource = 'online' | 'bootstrap';
export type TemplateProposalStatus = 'pending' | 'approved' | 'rejected' | 'superseded';

export interface StyleRule {
  id: string;
  user_id: string;
  user_email?: string;
  exam_category: string;
  trigger_phrase: string;
  replacement_phrase: string;
  occurrences: number;
  kept: number;
  confidence: number;
  state: StyleRuleState;
  source: StyleRuleSource;
  last_seen: string;
  activated_at?: string;
  retracted_at?: string;
  retraction_reason?: string;
}

export interface TemplateProposal {
  id: string;
  user_id: string;
  user_email?: string;
  exam_category: string;
  body: TemplateBody;
  derived_from_count: number;
  derived_by: string;
  derived_at: string;
  status: TemplateProposalStatus;
  reviewed_at?: string;
  review_notes?: string;
}

export interface TemplateBody {
  sections: Array<{
    name: string;
    fixed_prefix: string;
    normal_default: string;
    slot_name: string;
    conditional: boolean;
  }>;
  notes?: string;
}

export interface BanditCandidateMetric {
  rule_id: string;
  trigger_phrase: string;
  replacement_phrase: string;
  exam_category: string;
  trials_total: number;
  applied: { n: number; mean: number | null };
  not_applied: { n: number; mean: number | null };
  effect_size: number | null;  // negative = rule helps, positive = rule hurts
  days_collected: number | null;
  first_trial_at?: string;
  last_trial_at?: string;
}

export interface BanditMetrics {
  candidates: BanditCandidateMetric[];
  decision_thresholds: { min_trials_per_arm: number; min_effect_margin: number };
}

export interface LearningMetrics {
  window_days: number;
  rule_states: Record<StyleRuleState, number>;
  rule_buckets_top: Array<{ user_id: string; exam_category: string; active_rules: number }>;
  proposal_states: Record<TemplateProposalStatus, number>;
  vocab_profile_count: number;
  last_bootstrap_at?: string;
  edit_daily: Array<{ day: string; edits: number; avg_distance: number }>;
}

// ─── Workshop ──────────────────────────────────────────────────────

export type BacklogStatus = 'new' | 'triaged' | 'in_progress' | 'in_uat' | 'shipped' | 'wont_fix';
export type BacklogPriority = 'critical' | 'high' | 'medium' | 'low';
export type BacklogSource = 'feedback' | 'signal' | 'manual';
export type FeatureStatus = 'planned' | 'in_progress' | 'in_uat' | 'shipped' | 'cancelled';
export type UatStatus = 'not_tested' | 'pass' | 'fail' | 'blocked';
export type LineageEntityType = 'item' | 'feature' | 'uat';

export interface BacklogItem {
  id: string;
  title: string;
  description?: string;
  status: BacklogStatus;
  priority: BacklogPriority;
  source_type?: BacklogSource;
  source_id?: string;
  feature_id?: string;
  feature_name?: string;
  assigned_to?: string;
  created_at: string;
  updated_at: string;
}

export interface Feature {
  id: string;
  name: string;
  slug: string;
  problem_statement?: string;
  approach?: string;
  implementation_notes?: string;
  status: FeatureStatus;
  target_date?: string;
  created_at: string;
  updated_at: string;
  shipped_at?: string;
  item_count?: number;
  uat_pass_count?: number;
  uat_total_count?: number;
}

export interface UatCase {
  id: string;
  feature_id: string;
  title: string;
  steps?: string;
  expected_result?: string;
  status: UatStatus;
  notes?: string;
  last_tested_at?: string;
  last_tested_by?: string;
  created_at: string;
  updated_at: string;
}

export interface LineageEvent {
  id: string;
  entity_type: LineageEntityType;
  entity_id: string;
  entity_label?: string;
  from_state?: string;
  to_state: string;
  by_user?: string;
  by_user_email?: string;
  note?: string;
  created_at: string;
}

export interface FeatureDetail {
  feature: Feature;
  items: BacklogItem[];
  uat_cases: UatCase[];
  lineage: LineageEvent[];
}

export const BACKLOG_STATUS_LABEL: Record<BacklogStatus, string> = {
  new: 'New', triaged: 'Triaged', in_progress: 'In progress',
  in_uat: 'In UAT', shipped: 'Shipped', wont_fix: "Won't fix",
};

export const BACKLOG_PRIORITY_LABEL: Record<BacklogPriority, string> = {
  critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low',
};

export const FEATURE_STATUS_LABEL: Record<FeatureStatus, string> = {
  planned: 'Planned', in_progress: 'In progress', in_uat: 'In UAT',
  shipped: 'Shipped', cancelled: 'Cancelled',
};

export const UAT_STATUS_LABEL: Record<UatStatus, string> = {
  not_tested: 'Not tested', pass: 'Pass', fail: 'Fail', blocked: 'Blocked',
};

// Friendly labels for model names shown in the UI
export const MODEL_DISPLAY_NAMES: Record<string, string> = {
  'claude-opus-4-6': 'Claude Opus 4.6',
  'claude-sonnet-4-6': 'Claude Sonnet 4.6',
  'claude-haiku-4-5-20251001': 'Claude Haiku 4.5',
  'claude-opus-4-5-20251101': 'Claude Opus 4.5',
  'gpt-5.2': 'GPT-5.2 Thinking',
  'gpt-5.2-chat-latest': 'GPT-5.2 Instant',
  'gpt-4o': 'GPT-4o',
  'gpt-4o-mini': 'GPT-4o Mini',
};

export function modelLabel(model: string | undefined | null): string {
  if (!model) return '';
  return MODEL_DISPLAY_NAMES[model] || model;
}

export interface Report {
  id: string;
  user_id: string;
  transcription: string;
  generated_report: string;
  final_report?: string;
  modality: string;
  body_part?: string;
  patient_identifier?: string;
  study_date?: string;
  study_type?: string;
  clinical_history?: string;
  issues_detected?: ValidationIssue[];
  status: string;
  created_at: string;
  updated_at: string;
}

// Template types
export interface ReportTemplate {
  id: string;
  user_id?: string;
  name: string;
  modality: string;
  body_part?: string;
  exam_category?: string;
  prompt_text?: string;
  template_structure?: Record<string, unknown>;
  template_config?: Record<string, unknown>;
  description?: string;
  version: number;
  parent_template_id?: string;
  source?: string;
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface TemplateCreate {
  name: string;
  modality: string;
  body_part?: string;
  exam_category?: string;
  prompt_text?: string;
  template_structure?: Record<string, unknown>;
  template_config?: Record<string, unknown>;
  description?: string;
  is_default?: boolean;
}

export interface TemplateUpdate {
  name?: string;
  modality?: string;
  body_part?: string;
  exam_category?: string;
  prompt_text?: string;
  template_structure?: Record<string, unknown>;
  template_config?: Record<string, unknown>;
  description?: string;
  is_default?: boolean;
  is_active?: boolean;
}

export interface TemplateDecisionInfo {
  decisionId: string;
  examCategory: string;
  templateId?: string;
  templateName?: string;
  classificationMethod: string;
  classificationConfidence: number;
}

export interface ExamCategory {
  key: string;
  display_name: string;
  modality: string;
  body_part?: string;
  contrast?: boolean;
  protocol?: string;
}

// Preferences types
export interface Preferences {
  id: string;
  user_id: string;
  voice_input_mode: 'toggle' | 'push_to_talk' | 'pause_resume';
  transcription_mode: 'realtime' | 'batch';
  ui_theme: 'light' | 'dark';
  font_size: number;
  llm_verbosity: 'concise' | 'standard' | 'detailed';
  llm_formality: 'formal' | 'conversational';
  reporting_style: 'narrative' | 'structured';
  exam_notes: string;
  default_modality?: string;
  created_at: string;
  updated_at: string;
}

export interface PreferencesUpdate {
  voice_input_mode?: 'toggle' | 'push_to_talk' | 'pause_resume';
  transcription_mode?: 'realtime' | 'batch';
  ui_theme?: 'light' | 'dark';
  font_size?: number;
  llm_verbosity?: 'concise' | 'standard' | 'detailed';
  llm_formality?: 'formal' | 'conversational';
  reporting_style?: 'narrative' | 'structured';
  exam_notes?: string;
  default_modality?: string;
}

// Exam-specific note options available in Settings
export const EXAM_NOTE_OPTIONS = [
  {
    key: 'lumbar_spine_levels',
    label: 'Lumbar spine level-by-level organisation',
    description: 'Use level-by-level structure for lumbar spine cross-sectional imaging',
  },
] as const;

// Modality options
export const MODALITIES = [
  { value: 'CT', label: 'CT' },
  { value: 'MRI', label: 'MRI' },
  { value: 'XR', label: 'X-Ray' },
  { value: 'US', label: 'Ultrasound' },
  { value: 'Mammography', label: 'Mammography' },
  { value: 'NM', label: 'Nuclear Medicine' },
] as const;

export const BODY_PARTS: Record<string, string[]> = {
  CT: ['chest', 'abdomen', 'head', 'spine', 'neck'],
  MRI: ['brain', 'spine', 'knee', 'shoulder', 'pelvis', 'liver'],
  XR: ['chest', 'abdomen', 'spine', 'extremity'],
  US: ['abdomen', 'thyroid', 'pelvis', 'renal'],
  Mammography: ['breast'],
  NM: ['bone', 'thyroid', 'cardiac'],
};
